import { useQuery } from '@tanstack/react-query'
import { Clapperboard, Monitor, Radio } from 'lucide-react'
import { useMemo } from 'react'
import { Combobox, type ComboboxOption } from '@/components/combobox'
import { MultiCombobox } from '@/components/multi-combobox'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { useChannelOptions } from '@/features/channels/api'
import { useDisplayPage } from '@/features/displays/api'
import { useGroupOptions, useLocationComboOptions, useTagOptions } from '@/features/groups/components/pickers'
import { usePermission } from '@/hooks/use-permission'
import { api } from '@/lib/api'
import { cn } from '@/lib/utils'
import type { Paginated, ScheduleTargetType, Slideshow } from '@/types/api'
import { TARGET_TYPE_LABELS, type ScopedTargetType, type TargetSelection } from '../lib/schedule-form'

/** Up to 100 slideshows by name (published ones are playable). */
export function useSlideshowOptions(enabled = true) {
  return useQuery({
    queryKey: ['slideshows', 'list', 'schedule-picker'],
    queryFn: ({ signal }) => api.get<Paginated<Slideshow>>('/slideshows', { signal, query: { sort: 'name', per_page: 100 } }),
    select: (p) => p.data,
    enabled,
    staleTime: 30_000,
  })
}

/** Channel or published slideshow picker (channel-select style). */
export function ContentPicker({
  type,
  value,
  onChange,
  id,
  disabled,
  ...aria
}: {
  type: 'channel' | 'slideshow'
  value: string
  onChange: (id: string) => void
  id?: string
  disabled?: boolean
  'aria-invalid'?: boolean
  'aria-describedby'?: string
}) {
  const channels = useChannelOptions(type === 'channel')
  const slideshows = useSlideshowOptions(type === 'slideshow')
  const options = useMemo<ComboboxOption[]>(() => {
    if (type === 'channel')
      return (channels.data ?? []).map((c) => ({ value: c.id, label: c.name, hint: `${c.item_count} item${c.item_count === 1 ? '' : 's'}`, prefix: <Radio className="size-3.5 shrink-0 text-muted-foreground" /> }))
    // Only published slideshows can play; keep the current value visible even when it isn't.
    return (slideshows.data ?? [])
      .filter((s) => s.published_version || s.id === value)
      .map((s) => ({ value: s.id, label: s.name, hint: s.published_version ? `v${s.published_version.revision}` : 'unpublished', prefix: <Clapperboard className="size-3.5 shrink-0 text-muted-foreground" /> }))
  }, [type, channels.data, slideshows.data, value])
  const pending = type === 'channel' ? channels.isPending : slideshows.isPending
  const withCurrent = value && !options.some((o) => o.value === value) ? [{ value, label: pending ? 'Loading…' : `Unknown ${type}` }, ...options] : options
  return (
    <Combobox
      id={id}
      value={value || null}
      onChange={(v) => onChange(v ?? '')}
      options={withCurrent}
      placeholder={type === 'channel' ? 'Choose a channel' : 'Choose a published slideshow'}
      searchPlaceholder={type === 'channel' ? 'Search channels…' : 'Search slideshows…'}
      emptyText={pending ? 'Loading…' : type === 'channel' ? 'No channels yet' : 'No published slideshows yet'}
      disabled={disabled}
      {...aria}
    />
  )
}

export function useDisplayComboOptions(enabled = true): ComboboxOption[] {
  const displays = useDisplayPage({ per_page: 100, sort: 'name' }, enabled)
  return useMemo(
    () => (displays.data ?? []).map((d) => ({ value: d.id, label: d.name, hint: d.location?.name, prefix: <Monitor className="size-3.5 shrink-0 text-muted-foreground" /> })),
    [displays.data],
  )
}

/** All target options by type — for pickers and id → name lookups (422 messages). */
export function useTargetOptions(): Record<ScopedTargetType, ComboboxOption[]> {
  const canView = usePermission('displays.view')
  return {
    location: useLocationComboOptions(),
    display_group: useGroupOptions(),
    tag: useTagOptions(),
    display: useDisplayComboOptions(canView),
  }
}

export function targetLabel(options: Record<ScopedTargetType, ComboboxOption[]>, type: ScheduleTargetType, id: string | undefined): string | null {
  if (type === 'tenant' || !id) return null
  return options[type].find((o) => o.value === id)?.label ?? null
}

const TYPES: ScopedTargetType[] = ['location', 'display_group', 'tag', 'display']

/** "All displays" switch, or any mix of locations / groups / tags / displays. */
export function TargetsPicker({
  value,
  onChange,
  options,
  id,
  allowAll = true,
  ...aria
}: {
  value: TargetSelection
  onChange: (v: TargetSelection) => void
  options: Record<ScopedTargetType, ComboboxOption[]>
  id?: string
  allowAll?: boolean
  'aria-invalid'?: boolean
  'aria-describedby'?: string
}) {
  return (
    <div id={id} className="grid gap-3" role="group" aria-describedby={aria['aria-describedby']}>
      {allowAll && (
        <div className="flex items-center gap-3 rounded-lg border bg-muted/20 px-3 py-2.5">
          <Switch id={`${id}-all`} checked={value.all} onCheckedChange={(all) => onChange({ ...value, all })} />
          <Label htmlFor={`${id}-all`} className="cursor-pointer">
            All displays
          </Label>
          <span className="ml-auto text-xs text-muted-foreground">{value.all ? 'Organization-wide (class 200)' : 'Pick specific targets'}</span>
        </div>
      )}
      {!value.all && (
        <div className={cn('grid gap-3 sm:grid-cols-2')}>
          {TYPES.map((type) => (
            <div key={type} className="grid gap-1.5">
              <Label htmlFor={`${id}-${type}`}>{TARGET_TYPE_LABELS[type]}</Label>
              <MultiCombobox
                id={`${id}-${type}`}
                value={value[type]}
                onChange={(ids) => onChange({ ...value, [type]: ids })}
                options={options[type]}
                placeholder="None"
                searchPlaceholder={`Search ${TARGET_TYPE_LABELS[type].toLowerCase()}…`}
                emptyText="Nothing found"
                aria-invalid={aria['aria-invalid']}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
